import { z } from 'zod';
import { Id, IsoTime } from './api.js';
// Build chat (D-BUILD-CHAT), transcribed from T06 §4. Agent rows carry the writer's ULID; rows the API mints carry `m_<uuid>`.
export const Ulid = z.string().regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/);
export const ChatMessageId = z.union([Id, Ulid]);
export const ChatActor = z.enum(['creator', 'agent', 'system']);
export const ChatKind = z.enum(['text', 'image', 'clip', 'checkpoint', 'progress']);
export const ChatMedia = z.object({
    url: z.string().url(),
    poster: z.string().url().optional(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    duration_ms: z.number().int().nonnegative().optional(),
});
// `decision_id` is "" until OGM-082 correlates it; `media_id` names a BuildChatMessage.id in the same job.
export const ChatCheckpoint = z.object({
    decision_id: z.string(),
    question: z.string(),
    options: z.array(z.object({ id: z.string(), label: z.string(), media_id: z.string().optional() })),
    recommend: z.string().optional(),
    blocking: z.boolean().optional(),
    status: z.enum(['open', 'answered', 'moot']),
    option_id: z.string().optional(),
});
// Files a creator attaches to a chat message (#222). Images keep #214's 10 MiB cap and are re-encoded to WebP on upload;
// text files are UTF-8 and capped at 64 KB. Upload each with POST /api/v1/jobs/:jobId/chat/attachments, then name the
// returned ids in the message's `attachments`.
export const MAX_CHAT_ATTACHMENTS = 8;
export const MAX_TEXT_ATTACHMENT_BYTES = 64 * 1024;
export const MAX_IMAGE_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const TEXT_ATTACHMENT_TYPES = ['text/plain', 'text/markdown', 'application/json'];
export const ChatAttachmentKind = z.enum(['image', 'text']);
export const ChatAttachment = z
    .object({
    id: Id,
    kind: ChatAttachmentKind,
    name: z.string().min(1).max(200),
    mime: z.enum(['image/webp', ...TEXT_ATTACHMENT_TYPES]),
    bytes: z.number().int().positive().max(MAX_IMAGE_ATTACHMENT_BYTES),
})
    .strict();
export const BuildChatMessage = z.object({
    id: ChatMessageId,
    job_id: Id,
    at: IsoTime,
    actor: ChatActor,
    kind: ChatKind,
    text: z.string().optional(),
    media: ChatMedia.optional(),
    checkpoint: ChatCheckpoint.optional(),
    attachments: z.array(ChatAttachment).max(MAX_CHAT_ATTACHMENTS).optional(),
    aiur: z
        .object({ ticket: z.string(), event_id: z.number().int().optional(), topic: z.string().optional() })
        .optional(),
});
// One line of `<workspace>/.gm/outbox/*.jsonl`, written inside the no-network sandbox and shipped by the API's relay.
// Known types: chat.post (this context), checkpoint.open (OGM-082), scope.agreed (OGM-088), narration.render (OGM-087).
export const AgentOutboxLine = z.object({
    v: z.literal(1),
    id: Ulid,
    job_id: Id,
    at: IsoTime,
    type: z.string().regex(/^[a-z_]+\.[a-z_]+$/),
    payload: z.record(z.string(), z.unknown()),
});
export const CheckpointOpenPayload = z
    .object({
    checkpoint_id: z.string().regex(/^cp_[0-7][0-9A-HJKMNP-TV-Z]{25}$/),
    question: z.string().trim().min(1).max(8000),
    options: z
        .array(z.object({
        id: z.string().regex(/^[a-z0-9_-]{1,32}$/),
        label: z.string().trim().min(1).max(200),
        media_id: ChatMessageId.optional(),
    }))
        .min(2)
        .max(5),
    recommend: z
        .string()
        .regex(/^[a-z0-9_-]{1,32}$/)
        .optional(),
    blocking: z.boolean(),
})
    .strict()
    .superRefine((payload, ctx) => {
    const ids = payload.options.map(({ id }) => id);
    if (new Set(ids).size !== ids.length)
        ctx.addIssue({ code: 'custom', message: 'option ids must be unique' });
    if (payload.recommend !== undefined && !ids.includes(payload.recommend)) {
        ctx.addIssue({ code: 'custom', path: ['recommend'], message: 'recommend must name an option' });
    }
});
// `file` and `poster` are workspace-relative paths inside `.gm/outbox/media/`.
export const ChatPostPayload = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string().min(1).max(8000) }),
    z.object({
        kind: z.enum(['image', 'clip']),
        text: z.string().max(8000).optional(),
        file: z.string().min(1).max(512),
        poster: z.string().min(1).max(512).optional(),
    }),
]);
// POST /api/v1/jobs/:jobId/chat. aiur caps an operator message at 8,000 characters.
// `text` may be left out only when the message carries attachments.
export const ChatPostBody = z
    .object({
    text: z.string().regex(/\S/).trim().min(1).max(8000).optional(),
    attachments: z.array(Id).min(1).max(MAX_CHAT_ATTACHMENTS).optional(),
})
    .strict()
    .refine((body) => body.text !== undefined || body.attachments !== undefined, {
    message: 'text or attachments is required',
    path: ['text'],
})
    .refine((body) => new Set(body.attachments ?? []).size === (body.attachments?.length ?? 0), {
    message: 'attachment ids must be unique',
    path: ['attachments'],
});
// GET /api/v1/jobs/:jobId/chat/messages?since=<seq> → rows with seq > since, oldest first; pass `last_seq` back as `since`.
// SSE: GET /api/v1/jobs/:jobId/chat?since=<seq> → "id: <seq>\nevent: message\ndata: <BuildChatMessage JSON>"; Last-Event-ID resumes.
export const ChatSinceQuery = z.object({ since: z.coerce.number().int().nonnegative().default(0) });
export const ChatMessagesPage = z.object({
    messages: z.array(BuildChatMessage),
    last_seq: z.number().int().nonnegative(),
});
export const chatSchemas = {
    'chat.message.v1': BuildChatMessage,
    'chat.outbox-line.v1': AgentOutboxLine,
    'chat.checkpoint-open-payload.v1': CheckpointOpenPayload,
    'chat.post-payload.v1': ChatPostPayload,
    'chat.post.v1': ChatPostBody,
    'chat.messages.v1': ChatMessagesPage,
    'chat.attachment.v1': ChatAttachment,
};
//# sourceMappingURL=chat.js.map